import React from 'react';
import { twMerge } from 'tailwind-merge';

const FormTextArea = ({
    label,
    id,
    value,
    onChange,
    placeholder,
    required = false,
    error,
    helpText,
    className = '',
    wrapperClassName = '',
    rows = 4,
    maxLength,
    showCharacterCount = false,
    disabled = false,
    resize = true // 'none' | 'vertical' | 'horizontal' | true
}) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const characterCount = value?.length || 0;

    // Base textarea classes
    const baseTextareaClasses = `
        appearance-none
        block
        w-full
        px-3
        py-2
        border
        rounded-md
        shadow-sm
        text-sm
        placeholder-gray-400
        focus:outline-none
        focus:ring-2
        focus:ring-offset-0
        transition-colors
        disabled:bg-gray-50
        disabled:text-gray-500
        disabled:cursor-not-allowed
        ${error
            ? 'border-red-200 focus:border-red-300 focus:ring-red-300'
            : 'border-gray-200 focus:border-primary focus:ring-primary'
        }
        ${resize === true 
            ? 'resize'
            : resize === 'vertical'
                ? 'resize-y'
                : resize === 'horizontal'
                    ? 'resize-x'
                    : 'resize-none'
        }
    `;

    // Use twMerge to intelligently merge the classes
    const textareaClasses = twMerge(baseTextareaClasses, className);
    const containerClasses = twMerge('w-full', wrapperClassName);

    return (
        <div className={containerClasses}>
            {label && (
                <label
                    htmlFor={textareaId}
                    className="block text-sm font-medium text-gray-700 mb-1 ml-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <div className="relative">
                <textarea
                    id={textareaId}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    className={textareaClasses}
                    rows={rows}
                    maxLength={maxLength}
                    disabled={disabled}
                />
                {error && (
                    <div className="absolute top-2 right-2">
                        <svg
                            className="h-5 w-5 text-red-500"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </div>
                )}
            </div>
            <div className="flex justify-between items-center mt-1">
                <div>
                    {(error || helpText) && (
                        <p className={`text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
                            {error || helpText}
                        </p>
                    )}
                </div>
                {showCharacterCount && maxLength && (
                    <div className="text-sm text-gray-400">
                        {characterCount}/{maxLength}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormTextArea;